/**
 * AxisOrderEditor — User-konfigurierbare Reihenfolge der Tree-Achsen.
 *
 * Migration 0010 / 4-Achsen-Modell. Nutzt HTML5 Drag-and-Drop (kein extra dep wie @dnd-kit),
 * weil die Anzahl der Items klein und fest ist (6 Achsen). Persistiert via
 * PATCH /api/app-settings { axisOrder }.
 *
 * Validierung (Server): alle 6 Achsen, jede genau einmal.
 */
import { useState, useEffect } from "react";
import { Bars3Icon, ArrowPathIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import {
  useAppSettings,
  useUpdateAppSettings,
  DEFAULT_AXIS_ORDER,
  type AxisType,
} from "../lib/receipts";

const AXIS_LABEL: Record<AxisType, string> = {
  gruppe: "Gruppe der Buchungsart (§18 / §19 / §21 / … / Privat)",
  direction: "Einnahme / Ausgabe",
  stability: "Variabel / Fix",
  category: "Hauptkategorie",
  subcategory: "Unterkategorie",
  subSubcategory: "Sub-Unterkategorie",
};

export function AxisOrderEditor() {
  const { data: settings, isLoading } = useAppSettings();
  const updateSettings = useUpdateAppSettings();
  // Lokaler Draft, damit Drag-Reihenfolge optimistic gezeigt wird, bevor der Server bestätigt.
  const [draft, setDraft] = useState<AxisType[]>(DEFAULT_AXIS_ORDER);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Sync mit Server, wenn die Settings sich ändern (z.B. nach Reset).
  useEffect(() => {
    if (settings?.axisOrder?.length) setDraft(settings.axisOrder);
  }, [settings?.axisOrder]);

  const persist = (next: AxisType[]) => {
    setDraft(next);
    updateSettings.mutate({ axisOrder: next });
  };

  const onDragStart = (idx: number) => () => setDragIdx(idx);
  const onDragOver = (_idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
  };
  const onDrop = (targetIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null);
      return;
    }
    const next = [...draft];
    const [moved] = next.splice(dragIdx, 1);
    if (moved) next.splice(targetIdx, 0, moved);
    setDragIdx(null);
    persist(next);
  };
  const onDragEnd = () => setDragIdx(null);

  const reset = () => persist([...DEFAULT_AXIS_ORDER]);

  if (isLoading) return <div className="text-[13px] text-ios-muted">Lade Einstellungen …</div>;

  const isDirty =
    settings?.axisOrder &&
    (settings.axisOrder.length !== draft.length ||
      settings.axisOrder.some((v, i) => v !== draft[i]));

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold">Achsen-Reihenfolge im Tree</h3>
          <p className="text-[13px] text-ios-muted">
            Bestimmt, wie die Sidebar gegliedert ist. Ziehe die Zeilen, um die Reihenfolge zu ändern.
            Der Knoten ganz oben ist die oberste Ebene.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-1 text-[12px] text-ios-blue hover:underline whitespace-nowrap"
          title="Auf Standard zurücksetzen"
        >
          <ArrowPathIcon className="w-3.5 h-3.5" />
          Standard
        </button>
      </div>

      <ol className="space-y-1">
        {draft.map((axis, idx) => (
          <li
            key={axis}
            draggable
            onDragStart={onDragStart(idx)}
            onDragOver={onDragOver(idx)}
            onDrop={onDrop(idx)}
            onDragEnd={onDragEnd}
            className={clsx(
              "flex items-center gap-2 px-3 py-2 rounded-ios border bg-white/60",
              "cursor-move select-none transition",
              dragIdx === idx ? "opacity-50 border-ios-blue" : "border-ios-line/60 hover:border-ios-blue/40",
            )}
          >
            <Bars3Icon className="w-4 h-4 text-ios-muted shrink-0" />
            <span className="text-[12px] text-ios-muted w-5 shrink-0">{idx + 1}.</span>
            <span className="text-[14px] flex-1 truncate">{AXIS_LABEL[axis]}</span>
          </li>
        ))}
      </ol>

      {isDirty && (
        <div className="text-[12px] text-ios-muted">
          Speichern läuft … die Tree-Ansicht aktualisiert sich automatisch.
        </div>
      )}
      {updateSettings.isError && (
        <div className="text-[12px] text-ios-red">
          Speichern fehlgeschlagen — bitte erneut versuchen.
        </div>
      )}
    </div>
  );
}
